<template>
  <div id="widgets-payflex">
    <section>
      <span class="content__heading mb-3">
        Payflex BNPL Add-ons for your Website
      </span>
      <div class="small text-muted">
        Create a personalised widget for your website to show shoppers exactly
        what their purchase will cost with Payflex - split into easy,
        interest-free instalments.<br />
        By displaying flexible payment options upfront, you can build trust,
        reduce cart abandonment and boost conversions.
      </div>
    </section>

    <section>
      <p class="mt-4 mb-3">
        Preview:
      </p>

      <div class="border border-secondary-light py-4 px-4 mb-4" ref="widget-preview-script-cont" style="min-height: 130px; background: #fff">
        <div ref="widget-preview-script"></div>
      </div>
    </section>

    <section>
      <div class="pb-4">
        <p class="font-weight-bold mt-4 mb-1">Code Snippet:</p>

        <div class="small text-muted mb-2">
          Logo: {{ widget.logo_type || "default" }} |
          Plan: {{ widget.pay_type || "default" }}
        </div>

        <div class="mb-3 small text-muted">
          Copy the code snippet into your website linked to the relevant product display, and simply change the amount to the price of the product displayed.
          <a href="https://docs.payflex.co.za/docs/widgets" target="_blank" class="link-active">
            Learn More.
          </a>
        </div>

        <v-alert dismissible outlined text type="success" class="mt-2" v-show="snippetCopied">
          The Code Snippet has been successfully copied to your clipboard!
        </v-alert>

        <v-list dense class="d-inline-flex mb-3">
          <v-list-item dense class="v-list-item-alt text-capitalize font-weight-bold mr-2" @click="integrationCustom" :class="{ 'v-list-item-alt--active': isIntegrationCustom }">
            Custom
          </v-list-item>
          <v-list-item dense class="v-list-item-alt text-capitalize font-weight-bold mr-2" @click="integrationShopify" :class="{ 'v-list-item-alt--active': isIntegrationShopify }">
            Shopify
          </v-list-item>
          <v-list-item dense class="v-list-item-alt text-capitalize font-weight-bold" @click="integrationWoocommerce" :class="{ 'v-list-item-alt--active': isIntegrationWoocommerce }">
            WooCommerce
          </v-list-item>
        </v-list>

        <div class="d-lg-flex align-items-center justify-content-between"
             style="background-color: #f0f0f0; padding: 8px">
          <span class="text-code text-break text-monospace flex-grow-1">
            {{ snippet }}
          </span>
          <v-btn rounded color="primary" small @click="copySnippet">
            Copy code
          </v-btn>
        </div>
      </div>

      <hr class="border-bottom" />

      <div class="py-4">
        <span class="content__heading mb-3">Payflex Marketing Banner</span>
        <div class="small text-muted mb-3">
          <span>
            Download Payflex marketing material to promote this Buy Now, Pay Later payment method on your website.
          </span>
        </div>
        <div>
          <v-btn :href="marketingBannerPackLink" download rounded color="secondary" class="d-inline-flex py-5 px-8 text-capitalize" aria-label="Download">
            <span>
              <svg class="svg-inline--fa fa-download fa-w-16" aria-hidden="true" focusable="false" data-prefix="fa" data-icon="download" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" data-fa-i2svg="">
                <path fill="currentColor" d="M216 0h80c13.3 0 24 10.7 24 24v168h87.7c17.8 0 26.7 21.5 14.1 34.1L269.7 378.3c-7.5 7.5-19.8 7.5-27.3 0L90.1 226.1c-12.6-12.6-3.7-34.1 14.1-34.1H192V24c0-13.3 10.7-24 24-24zm296 376v112c0 13.3-10.7 24-24 24H24c-13.3 0-24-10.7-24-24V376c0-13.3 10.7-24 24-24h146.7l49 49c20.1 20.1 52.5 20.1 72.6 0l49-49H488c13.3 0 24 10.7 24 24zm-124 88c0-11-9-20-20-20s-20 9-20 20 9 20 20 20 20-9 20-20zm64 0c0-11-9-20-20-20s-20 9-20 20 9 20 20 20 20-9 20-20z">
                </path>
              </svg>
              Download
            </span>
          </v-btn>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "PayflexWidget",
  data() {
    return {
      snippet_copied: false,
      marketingBannerPackLink: "https://content.payfast.io/widgets/payflex/assets/Payflex_Marketing_Banner_Pack.zip",
      widget: {
        url: "https://widgets.payflex.co.za/your-merchant-name/payflex-widget-2.0.0.js",
        logo_type: "purple",
        pay_type: "4",
        integration: "custom",
        vars: ["logo_type", "pay_type"]
      }
    };
  },
  mounted() {
    const baseUrl = this.widget.url.split("?")[0];

    if (!document.querySelector(`script[src*="${baseUrl}"]`)) {
      const script = document.createElement("script");
      script.src = baseUrl;
      script.async = true;
      document.head.appendChild(script);
    }

    this.$nextTick(() => this.renderWidgetPreview());
  },
  computed: {
    isIntegrationCustom() {
      return this.widget.integration === "custom";
    },
    isIntegrationShopify() {
      return this.widget.integration === "shopify";
    },
    isIntegrationWoocommerce() {
      return this.widget.integration === "woocommerce";
    },
    snippetCopied() {
      return this.snippet_copied;
    },
    snippet() {
      return (
        '<script async src="' +
        this.snippetUrl +
        '" type="application/javascript"></scr' +
        "ipt>"
      );
    },
    snippetUrl() {
      const qs = { type: "calculator", amount: "--AMOUNT--" };
      const obj = this.widget;

      this.widget.vars.forEach((k) => {
        if (obj[k]) qs[k] = obj[k];
      });

      let link = this.widget.url + "?" + new URLSearchParams(qs).toString();

      const integrations = {
        shopify:
          "{{product.selected_or_first_available_variant.price | divided_by: 100}}",
        woocommerce: `<?php echo $product->get_price(); ?>`,
      };

      return link.replace(
        "--AMOUNT--",
        integrations[this.widget.integration] || "{product_price}"
      );
    },
  },
  methods: {
    copySnippet() {
      this.copyTextToClipboard(this.snippet);
    },
    copyTextToClipboard(text) {
      if (!navigator.clipboard) {
        this.fallbackCopyTextToClipboard(text);
        return;
      }

      navigator.clipboard.writeText(text).then(() => {
        this.snippet_copied = true;
        setTimeout(() => (this.snippet_copied = false), 4000);
      });
    },
    fallbackCopyTextToClipboard(text) {
      const area = document.createElement("textarea");
      area.value = text;
      document.body.appendChild(area);
      area.select();
      document.execCommand("copy");
      document.body.removeChild(area);
    },
    integrationCustom() {
      this.widget.integration = "custom";
    },
    integrationShopify() {
      this.widget.integration = "shopify";
    },
    integrationWoocommerce() {
      this.widget.integration = "woocommerce";
    },
    renderWidgetPreview() {
      const cont = this.$refs["widget-preview-script"];

      if (!cont) return;

      const script = document.createElement("script");
      script.async = true;
      script.type = "application/javascript";

      script.src = this.snippetUrl.replace("{product_price}", "1000");

      cont.appendChild(script);

      const amount = 1000;
      const payType = this.widget.pay_type || 4;

      if (window.PayflexWidget && window.PayflexWidget.update) {
        window.PayflexWidget.update(amount, Number(payType));
      }
    }
  }
};
</script>


<style lang="scss" scoped>
  svg:not(:root).svg-inline--fa {
    overflow: visible;
  }
  .svg-inline--fa.fa-w-16 {
    width: 1em;
  }
  .svg-inline--fa {
    display: inline-block;
    font-size: inherit;
    height: 1em;
    overflow: visible;
    vertical-align: -0.125em;
  }
</style>